import { types as api } from '@mesa-labs/mesa-api';
import * as types from '@mesa-labs/mesa-api/dist/types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Checkbox, DownloadButton, MesaColumn, MesaTable, Search, useMesaTableServerState } from '@mesa-labs/mesa-ui';

import { useLocation } from 'react-router-dom';
import { DefaultPaginationLimits } from './PaginationLimits';
import { exportAllExternalClients, useGetAllExternalClientsQuery } from '../../redux/api/externalVendors';
import { asExportFilter } from '../../utils';
import { useDispatch, useSelector } from '../../redux/hooks';
import {
  updatePoaQualified,
  updateSearchTerm,
} from '../../redux/slices/externalClients';
import { FilterCell, FilterControls, FilterRow, FilterSection, FilterTitle } from './Filters';
import PartnerSelect from './PartnerSelect';
import useSyncQueryStringParamsToRedux from '../../hooks/useSyncQueryStringParamsToRedux';

const ClientsPageContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableWrapper = styled.div`
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const TableCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 18px;
  border-bottom: 1px solid #E5E7EB;
`;

const TableCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
`;

const TableTitleIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background: #E0E7FF;
`;

const TableMeta = styled.span`
  font-size: 11.5px;
  font-weight: 400;
  color: #9CA3AF;
`;

// currencyCode is a per-column fallback since ExternalClientDataResponse
// carries no currency/currencyCode field of its own.
// TODO: MESA-1457: Multi-currency support
export const ExternalClientMesaColumns: MesaColumn<api.ExternalClientDataResponse>[] = [
  {
    id: 'partnerName',
    label: 'Partner',
    sortField: 'partnerId',
    accessorFn: (row) => api.Partners[row.partnerId],
  },
  {
    key: 'externalClientId',
    sortField: 'externalClientId',
    label: 'External Client Id',
  },
  {
    key: 'externalClientName',
    sortField: 'externalClientName',
    label: 'External Client Name',
    flex: true,
  },
  {
    key: 'segment',
    sortField: 'segment',
    label: 'Client Segment',
  },
  {
    key: 'totalInvoiceSpendT12m',
    sortField: 'totalInvoiceSpendT12m',
    label: 'Total Invoice Spend (T12M)',
    type: 'currency',
    currencyCode: 'USD',
  },
  {
    key: 'weightedAveragePaymentTenorT12m',
    sortField: 'weightedAveragePaymentTenorT12m',
    label: 'Weighted APT (T12M)',
    type: 'number',
  },
  {
    key: 'exclusionType',
    label: 'Exclusion Type',
  },
];

type ExternalClientVendorsPageTemplate = {
  businessEntityScope: api.BusinessEntityScope;
};

function ExternalClientsPageTemplate(props: ExternalClientVendorsPageTemplate): React.ReactElement {
  const { businessEntityScope } = props;
  const qualifier = businessEntityScope === api.BusinessEntityScope.External ? 'external' : 'internal';

  const dispatch = useDispatch();
  const [partner, setPartner] = useState<api.Partners>(api.Partners.JLL);
  const searchTerm = useSelector((state) => state.externalClients.searchTerm);
  const poaQualified = useSelector((state) => state.externalClients.poaQualified);

  useSyncQueryStringParamsToRedux({ sliceName: 'externalClients' });

  const server = useMesaTableServerState({
    initialPageSize: 10,
    columns: ExternalClientMesaColumns,
    persistKey: `externalClientsPage-${qualifier}`,
  });

  // page/limit/sort left the externalClients slice for
  // useMesaTableServerState, so useSyncQueryStringParamsToRedux can no longer
  // deep-link them — bridge those four URL params into the hook once on
  // mount. Order matters here: the hook's reducer resets pageIndex to 0
  // whenever sorting or page size changes (by design, elsewhere in the app),
  // so setPageNumber must run last or a `page` param gets silently clobbered
  // back to the first page.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlPage = params.get('page');
    const urlLimit = params.get('limit');
    const urlSortField = params.get('sortField');
    const urlSortDirection = params.get('sortDirection');
    if (urlSortField) server.setSorting([{ id: urlSortField, desc: urlSortDirection === 'desc' }]);
    if (urlLimit) server.setPageSize(Number(urlLimit));
    if (urlPage) server.setPageNumber(Number(urlPage));
  }, []);

  const [searchInput, setSearchInput] = useState<string | undefined>(searchTerm);

  const filter: types.ExternalClientFilterParams & { partnerId: number } = {
    ...server.queryArgs,
    sortDirection: server.queryArgs.sortDirection as api.SortDirection | undefined,
    partnerId: partner,
    search: searchTerm,
    businessEntityScope,
    poaQualified,
  };

  const {
    data: {
      data: clients = [],
      total: totalClients,
    } = {},
    isLoading,
    isFetching,
  } = useGetAllExternalClientsQuery(filter);

  return (
    <ClientsPageContainer>
      <FilterSection
        filterControl={(
          <FilterControls>
            <Search
              placeholder="Search by External Client Name"
              value={searchInput || ''}
              onChange={(value) => setSearchInput(value)}
              onSubmit={(value) => {
                dispatch(updateSearchTerm(value));
                server.setPageNumber(1);
              }}
              onFinalize={(value) => {
                dispatch(updateSearchTerm(value));
                server.setPageNumber(1);
              }}
              expanded
              width="550px"
            />
            {/* TODO: remove ! once backend supports optional partner ID */}
            <DownloadButton
              text="Download CSV"
              data={() => exportAllExternalClients(partner!, {
                ...asExportFilter(filter),
              })}
              fileName="external-clients.csv"
              display="purple"
            />
          </FilterControls>
        )}
      >
        <FilterRow>
          <FilterCell>
            <Checkbox
              label="POA Qualified Only?"
              checked={poaQualified || false}
              onChange={() => dispatch(updatePoaQualified(!(poaQualified || false)))}
            />
          </FilterCell>
        </FilterRow>
        <FilterRow>
          <FilterCell>
            <FilterTitle>Partner</FilterTitle>
            <PartnerSelect
              selectedPartner={partner}
              onSelectedPartnerChange={(p) => setPartner(p!)}
            />
          </FilterCell>
        </FilterRow>
      </FilterSection>

      <TableWrapper>
        <TableCardHeader>
          <TableCardTitle>
            <TableTitleIcon />
            {qualifier === 'external' ? 'External Clients' : 'Internal Clients'}
            {totalClients !== undefined && (
              <TableMeta>{totalClients.toLocaleString()} results</TableMeta>
            )}
          </TableCardTitle>
        </TableCardHeader>
        <MesaTable
          columns={ExternalClientMesaColumns}
          data={clients}
          dynamicColumnMode
          getRowId={(row) => `${row.partnerId}:${row.externalClientId}`}
          customStyles={{
            columnFontSize: '10.5px',
            columnFontWeight: 700,
            columnColor: '#6B7280',
          }}
          getRowHref={(row) => `/partners/${row.partnerId}/${qualifier}-clients/${row.externalClientId}`}
          isLoading={isLoading || isFetching}
          {...server.bind}
          pagination={{
            ...server.bind.pagination,
            total: totalClients,
            pageSizeOptions: DefaultPaginationLimits.map((l) => l.value),
          }}
          columnLayout={{ enableResizing: true, enableVisibilityToggle: true, enablePinning: true }}
          minColumnWidth={90}
        />
      </TableWrapper>
    </ClientsPageContainer>
  );
}

export default ExternalClientsPageTemplate;
